import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardMedia,
  CardActions,
  CardContent,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Skeleton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Close,
  Upload,
  Save,
  ViewCarousel
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { blue, grey } from '@mui/material/colors';
import pb from './pocketbase';

// Styled components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const BannerCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  height: 200,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[8],
    '& .banner-actions': {
      opacity: 1
    }
  }
}));

const BannerActions = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(1),
  background: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  justifyContent: 'flex-end',
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  zIndex: 1
}));

const UploadBox = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: theme.palette.background.default,
  cursor: 'pointer',
  transition: 'border-color 0.2s ease-in-out, background-color 0.2s ease-in-out',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover
  }
}));

const Banners = () => {
  // Current date and user
  const currentDateTime = "2025-04-03 06:48:06";
  const currentUser = "MdAbuNafisNiloy";

  // State management
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);
  const [image, setImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [currentBannerId, setCurrentBannerId] = useState(null);
  const [formData, setFormData] = useState({
    Title: '',
    link: ''
  });
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [viewBannerDialog, setViewBannerDialog] = useState(false);
  const [currentBanner, setCurrentBanner] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch banners from PocketBase
  const fetchBanners = async () => {
    try {
      setLoading(true);
      const result = await pb.collection('banners').getList(1, 100, {
        sort: '-created'
      });
      setBanners(result.items);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching banners:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load banners',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  // Load data when component mounts
  useEffect(() => {
    fetchBanners();
  }, []);

  // Handle banner image change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: 'Image size should be less than 5MB',
          severity: 'error'
        });
        return;
      }
      
      setImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Open dialog to add a new banner
  const handleAddBanner = () => {
    setPreviewImage(null);
    setImage(null);
    setFormData({
      Title: '',
      link: ''
    });
    setDialogMode('add');
    setDialogOpen(true);
  };

  // Open dialog to edit a banner
  const handleEditBanner = (banner) => {
    setCurrentBannerId(banner.id);
    setFormData({
      Title: banner.Title || '',
      link: banner.link || ''
    });
    
    if (banner.image) {
      setPreviewImage(`${pb.baseUrl}/api/files/${banner.collectionId}/${banner.id}/${banner.image}`);
    } else {
      setPreviewImage(null);
    }
    
    setImage(null);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  // Open confirmation dialog to delete a banner
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setConfirmDeleteDialog(true);
  };

  // Delete a banner
  const handleDeleteConfirm = async () => {
    try {
      setIsSubmitting(true);
      
      await pb.collection('banners').delete(deleteId);
      
      setSnackbar({
        open: true,
        message: 'Banner deleted successfully',
        severity: 'success'
      });
      
      fetchBanners();
      setConfirmDeleteDialog(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error deleting banner:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete banner',
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // View banner details
  const handleViewBanner = (banner) => {
    setCurrentBanner(banner);
    setViewBannerDialog(true);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save banner
  const handleSaveBanner = async () => {
    try {
      setIsSubmitting(true);
      
      // Validate
      if (!image && dialogMode === 'add') {
        setSnackbar({
          open: true,
          message: 'Please select an image',
          severity: 'error'
        });
        setIsSubmitting(false);
        return;
      }
      
      // Create form data
      const formDataToSend = new FormData();
      formDataToSend.append('Title', formData.Title);
      formDataToSend.append('link', formData.link);
      
      if (image) {
        formDataToSend.append('image', image);
      }
      
      if (dialogMode === 'add') {
        // Create new banner
        await pb.collection('banners').create(formDataToSend);
        
        setSnackbar({
          open: true,
          message: 'Banner added successfully',
          severity: 'success'
        });
      } else {
        // Update existing banner
        await pb.collection('banners').update(currentBannerId, formDataToSend);
        
        setSnackbar({
          open: true,
          message: 'Banner updated successfully',
          severity: 'success'
        });
      }
      
      fetchBanners();
      setDialogOpen(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error saving banner:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${dialogMode === 'add' ? 'add' : 'update'} banner`,
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // Format date function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Banners
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage promotional banners for your application
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddBanner}
          sx={{ borderRadius: 2 }}
        >
          Add Banner
        </Button>
      </Box>

      {/* Banners Grid */}
      {loading ? (
        <Grid container spacing={3}>
          {Array.from(new Array(4)).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 4 }} />
            </Grid>
          ))}
        </Grid>
      ) : banners.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4 }}>
          <ViewCarousel sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Banners Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add your first banner to showcase in your application
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={handleAddBanner}
          >
            Add Banner
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {banners.map((banner) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={banner.id}>
              <BannerCard elevation={3}>
                <CardMedia
                  component="img"
                  height="200"
                  image={`${pb.baseUrl}/api/files/${banner.collectionId}/${banner.id}/${banner.image}`}
                  alt={banner.Title || 'Banner'}
                  onClick={() => handleViewBanner(banner)}
                  sx={{ cursor: 'pointer' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/400x200?text=Image+Not+Found';
                  }}
                />
                <BannerActions className="banner-actions">
                  <Tooltip title="View">
                    <IconButton 
                      size="small" 
                      sx={{ color: 'white' }}
                      onClick={() => handleViewBanner(banner)}
                    >
                      <ViewCarousel fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton 
                      size="small" 
                      sx={{ color: 'white' }}
                      onClick={() => handleEditBanner(banner)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton 
                      size="small" 
                      sx={{ color: 'white' }}
                      onClick={() => handleDeleteClick(banner.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </BannerActions>
              </BannerCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add/Edit Banner Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => !isSubmitting && setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {dialogMode === 'add' ? 'Add New Banner' : 'Edit Banner'}
            </Typography>
            <IconButton
              onClick={() => setDialogOpen(false)}
              disabled={isSubmitting}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Banner Image
              </Typography>
              
              {previewImage ? (
                <Box sx={{ position: 'relative', mb: 2 }}>
                  <img
                    src={previewImage}
                    alt="Banner preview"
                    style={{ 
                      width: '100%', 
                      height: 'auto', 
                      maxHeight: 300,
                      objectFit: 'contain',
                      borderRadius: 8
                    }}
                  />
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      component="label"
                    >
                      Change Image
                      <VisuallyHiddenInput 
                        type="file" 
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setPreviewImage(null);
                        setImage(null);
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              ) : (
                <UploadBox
                  component="label"
                  sx={{ mb: 2 }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageChange}
                  />
                  <Upload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" gutterBottom>
                    Click or drag to upload banner image
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recommended size: 1200 x 400 pixels, max 5MB
                  </Typography>
                </UploadBox>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                name="Title"
                value={formData.Title}
                onChange={handleInputChange}
                placeholder="Banner title (optional)"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Link URL"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                placeholder="https://example.com (optional)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveBanner}
            disabled={isSubmitting || (dialogMode === 'add' && !image)}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
          >
            {isSubmitting ? 'Saving...' : 'Save Banner'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Banner Dialog */}
      <Dialog 
        open={viewBannerDialog} 
        onClose={() => setViewBannerDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {currentBanner && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {currentBanner.Title || 'Banner'}
                </Typography>
                <IconButton onClick={() => setViewBannerDialog(false)}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <img
                  src={`${pb.baseUrl}/api/files/${currentBanner.collectionId}/${currentBanner.id}/${currentBanner.image}`}
                  alt={currentBanner.Title || 'Banner'}
                  style={{ 
                    width: '100%', 
                    height: 'auto', 
                    maxHeight: 400,
                    objectFit: 'contain',
                    borderRadius: 8
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/800x400?text=Image+Not+Found';
                  }}
                />
              </Box>
              
              <Grid container spacing={2}>
                {currentBanner.Title && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Title
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {currentBanner.Title}
                    </Typography>
                  </Grid>
                )}
                
                {currentBanner.link && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Link
                    </Typography>
                    <Typography 
                      variant="body1" 
                      component="a"
                      href={currentBanner.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: blue[600],
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {currentBanner.link}
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(currentBanner.created)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(currentBanner.updated)}
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={() => setViewBannerDialog(false)}
              >
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => {
                  setViewBannerDialog(false);
                  handleEditBanner(currentBanner);
                }}
              >
                Edit
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteDialog}
        onClose={() => !isSubmitting && setConfirmDeleteDialog(false)}
      >
        <DialogTitle>
          Delete Banner
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete this banner? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteDialog(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Delete />}
          >
            {isSubmitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Banners;